// Thin wrapper around the relayer REST API. Mirrors `docs/relayer-api.md`.
//
// All exported functions accept an optional `endpoint` argument that
// overrides the configured `RELAYER_URL`. This lets the dApp talk to a
// user-selected relayer (e.g. one discovered via the on-chain registry)
// without changing global config.

import { RELAYER_URL } from "./env";

export type RelayerStatus = {
  network: "mainnet" | "testnet";
  feeBps: number;
  feeMinNanoTon: string;
  relayerAddress: string;
};

export type NoteCheckResult = {
  deposited: boolean;
  leafIndex: number | null;
  timestamp: number | null;
  spent: boolean;
};

async function api<T>(base: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    headers: { "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`relayer ${res.status}: ${body || res.statusText}`);
  }
  return res.json();
}

export function getStatus(endpoint: string = RELAYER_URL): Promise<RelayerStatus> {
  return api<RelayerStatus>(endpoint, "/status");
}

export function checkNote(
  pool: string,
  payload: { commitment?: string; nullifierHash?: string },
  endpoint: string = RELAYER_URL
): Promise<NoteCheckResult> {
  return api<NoteCheckResult>(endpoint, "/note/check", {
    method: "POST",
    body: JSON.stringify({ pool, ...payload }),
  });
}

export type BroadcastPayload = {
  pool: string;
  proof: unknown;
  publicSignals: string[];
  recipient: string;
  fee: string;
  refund: string;
};

export function broadcastWithdraw(
  payload: BroadcastPayload,
  endpoint: string = RELAYER_URL
): Promise<{ seqno: number; fee: string }> {
  return api(endpoint, "/withdraw", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
